/**
 * Change Pay (piece 34, *Compensation*; decision 3781). 🔒 A pay change is a
 * new dated `CompensationRecord` row, entered on the Employee page by a holder
 * of `hr.hire` and written Pending. 🔨 A second holder approves it there, never
 * the person who entered it and never the employee whose pay it is; approval
 * closes the row in force the day before the new one starts. It never enters
 * the Pending Approvals queue, because the queue lists to every viewer.
 */

import { payRateProblem, type PayRateUnitName } from './hire';
import { hasDayShape } from '../zonedTime';

/** Change Pay's write: a Pending pay row on the job. */
export function payChangePath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/pay`;
}

export const PAY_DECISIONS = ['approve', 'reject'] as const;
export type PayDecision = (typeof PAY_DECISIONS)[number];

/** A second holder's decision on one waiting pay row. */
export function payDecisionPath(employmentId: string, compensationId: string, decision: PayDecision): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/pay/${encodeURIComponent(compensationId)}/${decision}`;
}

/** The refusal Change Pay and its decision answer a viewer without the key, in a sentence. */
export const PAY_CHANGE_REFUSAL = 'Changing or approving pay is open to a holder of the hire permission, and this identity holds none.';

/** The longest reason a pay change or a rejection keeps. */
export const PAY_REASON_MAXIMUM = 500;

/** Change Pay's window, as the browser sends it. */
export interface PayChangeDraft {
  payRate: string;
  payRateUnit: PayRateUnitName | '';
  hoursPerPayPeriod: string;
  /** The first day the new rate is paid, as an ISO calendar day. */
  effectiveFrom: string;
  changeReason: string;
}

export const EMPTY_PAY_CHANGE: PayChangeDraft = { payRate: '', payRateUnit: '', hoursPerPayPeriod: '', effectiveFrom: '', changeReason: '' };

/** What the server knows about the job that the window's own check cannot. */
export interface PayChangeFacts {
  /** The job's first and last day; a change starts inside them. */
  jobStart: string;
  jobEnd: string | null;
  /** The start of the Approved row in force, which the change must follow. */
  inForceFrom: string | null;
  /** A change already waits on the job. */
  pendingWaiting: boolean;
}

/** 🔒 The sentence a second change on a job reads, from the count before the write and from the database's index under it (T2348). */
export const PAY_WAITING_REFUSAL = 'A pay change already waits on this job; it is approved or rejected before another is entered.';

/**
 * 🔒 Where an approved pay row stands against today in the organization's zone (T2348): its dates, never an empty end date alone,
 * so a raise approved ahead of its start reads as starting later and the row it will close reads as current until then.
 */
export function payRowStanding(effectiveFrom: string, effectiveTo: string | null, today: string): 'current' | 'ended' | 'later' {
  if (effectiveFrom > today) return 'later';
  if (effectiveTo !== null && effectiveTo < today) return 'ended';
  return 'current';
}

/** 🔒 A pay change's problems, each one sentence; the window asks without the facts and the route with them. */
export function payChangeProblems(draft: PayChangeDraft, facts?: PayChangeFacts): string[] {
  const problems: string[] = [];
  const rate = payRateProblem(draft.payRate);
  if (rate) problems.push(rate);
  if (!draft.payRateUnit) problems.push('The pay rate is per hour or per year.');
  if (draft.hoursPerPayPeriod.trim() && !/^\d+(\.\d{1,2})?$/.test(draft.hoursPerPayPeriod.trim())) problems.push('The hours a pay period are a number.');
  if (!hasDayShape(draft.effectiveFrom)) problems.push('The change needs the day it starts.');
  if (!draft.changeReason.trim()) problems.push('The change needs a reason.');
  else if (draft.changeReason.trim().length > PAY_REASON_MAXIMUM) problems.push(`The reason is ${PAY_REASON_MAXIMUM} characters or fewer.`);
  if (!facts) return problems;
  if (facts.pendingWaiting) problems.push(PAY_WAITING_REFUSAL);
  if (hasDayShape(draft.effectiveFrom)) {
    if (draft.effectiveFrom < facts.jobStart) problems.push(`A pay change starts on or after the job's first day, ${facts.jobStart}.`);
    if (facts.jobEnd !== null && draft.effectiveFrom > facts.jobEnd) problems.push(`A pay change starts on or before the job's last day, ${facts.jobEnd}.`);
    if (facts.inForceFrom !== null && draft.effectiveFrom <= facts.inForceFrom) problems.push(`A pay change starts after the rate in force started, on ${facts.inForceFrom}.`);
  }
  return problems;
}

/**
 * 🔨 Who may decide one waiting pay row, or the sentence that says why not
 * (decision 3781): a holder of the hire permission who neither entered the
 * change nor is paid by it. No setting and no administrator's standing turns
 * either refusal off.
 */
export function payDecisionProblem(input: {
  createdByPersonId: string | null;
  subjectPersonId: string;
  deciderPersonId: string;
  approvalStatus: string;
  holdsCapability: boolean;
}): string | null {
  if (!input.holdsCapability) return PAY_CHANGE_REFUSAL;
  if (input.approvalStatus !== 'Pending') return `This pay change is already ${input.approvalStatus}.`;
  if (input.createdByPersonId === input.deciderPersonId) return 'You entered this pay change, so a second holder of the hire permission decides it.';
  if (input.subjectPersonId === input.deciderPersonId) return 'A change to your own pay is decided by somebody else.';
  return null;
}

/** A rejection says why, and the reason is kept on the row. */
export function payRejectionProblem(reason: string | null | undefined): string | null {
  const text = (reason ?? '').trim();
  if (!text) return 'A rejected pay change says why.';
  return text.length > PAY_REASON_MAXIMUM ? `The reason is ${PAY_REASON_MAXIMUM} characters or fewer.` : null;
}

/** The waiting change, as the Pay section offers it. */
export interface PendingPayChange {
  id: string;
  /** Whether this reader may approve or reject it. */
  mayDecide: boolean;
  /** Why not, in a sentence, where the reader holds the key and still may not. */
  whyNot: string | null;
}

/** 🔒 The Pay section's controls, sent only to a holder of `hr.hire` reading as HR (decision 3781). */
export interface PaySection {
  /** Change Pay draws: an open job with no change waiting. */
  mayChange: boolean;
  /** The window's starting values: the rate in force, its unit and hours. */
  start: PayChangeDraft;
  pending: PendingPayChange | null;
}
